/**
 * La coquille d'un formulaire — la bibliothèque de formulaires, câblée.
 *
 * React Hook Form : la validation, l'état de soumission et la liaison des
 * champs sont à lui. Ce fichier n'en garde que ce qu'un squelette doit
 * garantir, et que chaque écran métier hériterait sinon de travers :
 *
 *  - **aucun libellé écrit ici** — chaque champ porte une clé de traduction ;
 *  - **un message d'erreur relié au champ** par `aria-describedby`, sinon le
 *    lecteur d'écran annonce un champ invalide sans dire pourquoi ;
 *  - **le bouton se désactive pendant la soumission**, avec un libellé
 *    distinct : c'est la garde contre le double envoi, exigée sur toute
 *    opération d'argent et posée ici une fois pour toutes.
 *
 * **Aucun écran métier.** La coquille reçoit la description de ses champs.
 *
 * ## Trois natures de champ, et le choix est celui de la DONNÉE
 *
 * La coquille ne rendait qu'un `<input type="text">`, et c'est ce que le
 * porteur a vu dans Chrome : une liste fermée de valeurs offerte en zone de
 * texte nue, une date à composer à la main. Un champ dit maintenant sa nature,
 * et la coquille appelle le contrôle partagé qui convient :
 *
 *  - `text` — la nature par défaut, la seule qui convienne à un texte **libre**
 *    (une référence d'entité, un motif) ;
 *  - `select` — `FilterSelect`, dès que les valeurs possibles forment une liste
 *    **fermée et connue** ;
 *  - `date` — `FilterDateField`, parce qu'une date ne se tape jamais.
 *
 * Le critère est la donnée, pas le goût de l'écran : un champ à liste fermée
 * rendu en texte libre laisse saisir ce qui n'existe pas, et le refus arrive du
 * serveur, après coup, sans dire quelles valeurs existaient.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';

import { useForm } from 'react-hook-form';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterDateField } from '../controls/FilterDateField.js';
import { FilterSelect } from '../controls/FilterSelect.js';
import type { FilterSelectOption } from '../controls/FilterSelect.js';
import { FilterTextField } from '../controls/FilterTextField.js';

/** La nature d'un champ — voir l'en-tête : c'est la donnée qui la décide. */
export type FormFieldControl = 'text' | 'select' | 'date';

/** Un champ du formulaire. */
export interface FormFieldDescriptor {
  /** Nom technique, employé comme clé de valeur. */
  readonly name: string;
  /** Clé de traduction de son libellé. */
  readonly labelKey: string;
  /** Champ obligatoire ? */
  readonly required?: boolean;
  /** Sa nature. Absente, c'est `text` — le texte libre. */
  readonly control?: FormFieldControl;
  /** Les valeurs d'une liste fermée. Obligatoire — et non vide — si `select`. */
  readonly options?: readonly FilterSelectOption[];
  /**
   * Clé de traduction de l'invite — ce qui s'affiche tant que rien n'est
   * choisi. Obligatoire pour `select` comme pour `date`.
   */
  readonly placeholderKey?: string;
}

/**
 * La disposition du formulaire.
 *
 * `stacked` — la disposition par défaut : des blocs empilés sur toute la
 * largeur. C'est celle d'une connexion, d'une demande de clôture — un
 * formulaire qu'on remplit une fois.
 *
 * `inline` — **la rangée de filtres du dessin** : tous les champs sur une seule
 * ligne, avec le bouton, en tête de la carte. Le dessin ne pose jamais trois
 * blocs empilés au-dessus de sa table ; il pose une rangée de pilules, et c'est
 * ce qui distingue le plus un écran de back-office d'un formulaire. En
 * disposition `inline`, la saisie libre prend elle aussi la pilule : un champ
 * de 48 points au milieu de pilules de 32 casserait la rangée à lui seul.
 */
export type FormLayout = 'stacked' | 'inline';

/** Ce que la coquille attend. */
export interface FormShellProps {
  readonly fields: readonly FormFieldDescriptor[];
  readonly submitLabelKey?: string;
  readonly onSubmit: (values: Record<string, string>) => void | Promise<void>;
  /** Sa disposition. Absente, c'est `stacked`. */
  readonly layout?: FormLayout;
  /**
   * Les valeurs que le formulaire **affiche**, quand c'est le serveur qui les
   * décide.
   *
   * Passées à `useForm({ values })` et non à `defaultValues` : les secondes ne
   * valent qu'au premier rendu, or ce qu'on veut afficher n'arrive qu'avec la
   * réponse. C'est le besoin du journal d'audit, où l'on peut lister **sans
   * rien saisir** — le serveur applique alors la journée courante, et les
   * pilules doivent montrer la fenêtre qu'il a appliquée plutôt que rester
   * vides sous une table qui, elle, montre une journée précise.
   *
   * La remise à jour suit la **référence** de l'objet : la garder stable entre
   * deux réponses laisse intactes les saisies en cours, et n'écrase le
   * formulaire qu'au moment où le serveur a effectivement répondu autre chose.
   */
  readonly values?: Record<string, string>;
}

/** Un formulaire dont les libellés viennent tous des traductions. */
export function FormShell({
  fields,
  submitLabelKey,
  onSubmit,
  layout = 'stacked',
  values,
}: FormShellProps): ReactNode {
  const { t } = useTranslation();
  const identifiant = useId();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Record<string, string>>(
    values === undefined ? { mode: 'onSubmit' } : { mode: 'onSubmit', values },
  );
  // Les deux contrôles bâtis ne sont pas des champs natifs : ils rendent un
  // déclencheur, pas un `<input>` que le navigateur remplirait. Leur valeur vit
  // donc dans le formulaire — `watch` la relit, `setValue` la pose — et un
  // champ caché la porte, pour que la validation « obligatoire » et l'envoi
  // les voient comme n'importe quel autre champ.
  const valeurs = watch();

  return (
    <form
      className={layout === 'inline' ? 'form form--inline' : 'form'}
      noValidate
      onSubmit={(evenement) => {
        void handleSubmit(onSubmit)(evenement);
      }}
    >
      {fields.map((champ) => {
        const idChamp = `${identifiant}-${champ.name}`;
        const idErreur = `${idChamp}-error`;
        const erreur = errors[champ.name];
        const liaison: UseFormRegisterReturn = register(champ.name, {
          required: champ.required === true ? t('form.required') : false,
        });
        const decrit = erreur === undefined ? undefined : idErreur;
        return (
          <div
            className={
              layout === 'inline' && (champ.control ?? 'text') === 'text'
                ? 'form__field form__field--growing'
                : 'form__field'
            }
            key={champ.name}
          >
            {rendreLeControle(champ, {
              idChamp,
              invalide: erreur !== undefined,
              decrit,
              liaison,
              libelle: t(champ.labelKey),
              enPilule: layout === 'inline',
              valeur: valeurs[champ.name] ?? '',
              poser: (choisie: string) => {
                setValue(champ.name, choisie, { shouldValidate: true });
              },
            })}
            {erreur === undefined ? null : (
              <p className="form__error" id={idErreur} role="alert">
                {String(erreur.message)}
              </p>
            )}
          </div>
        );
      })}

      <button className="form__submit" type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('form.submitting') : t(submitLabelKey ?? 'form.submit')}
      </button>
    </form>
  );
}

/** Ce dont un champ a besoin pour se rendre, quelle que soit sa nature. */
interface ContexteDeChamp {
  readonly idChamp: string;
  readonly invalide: boolean;
  readonly decrit: string | undefined;
  readonly liaison: UseFormRegisterReturn;
  /** Le libellé **déjà traduit** — les contrôles partagés, eux, prennent la clé. */
  readonly libelle: string;
  /** La saisie libre prend-elle la pilule ? Vrai en rangée de filtres. */
  readonly enPilule: boolean;
  /** La valeur courante du champ, telle que le formulaire la tient. */
  readonly valeur: string;
  /** Pose une valeur choisie, et revalide : le champ cesse d'être en erreur. */
  readonly poser: (choisie: string) => void;
}

/**
 * Rend le contrôle qui convient à la nature du champ.
 *
 * Le texte libre garde exactement le balisage qu'il avait — `.form__label` et
 * `.form__input`, l'erreur reliée par `aria-describedby` — parce que rien ne
 * lui reprochait sa forme : un motif de consultation *est* du texte libre. Les
 * deux autres natures délèguent aux contrôles partagés, qui portent la pilule
 * du dessin et l'association du libellé.
 */
function rendreLeControle(champ: FormFieldDescriptor, contexte: ContexteDeChamp): ReactNode {
  const { idChamp, invalide, decrit, liaison, libelle, enPilule, valeur, poser } = contexte;
  const invite = (): string => {
    if (champ.placeholderKey === undefined) {
      // Sans invite, le déclencheur n'aurait rien à afficher tant que rien
      // n'est choisi — et un déclencheur vide se lit comme un champ cassé.
      throw new Error(`ÉCHEC : le champ « ${champ.name} » est un contrôle sans clé d’invite.`);
    }
    return champ.placeholderKey;
  };
  switch (champ.control ?? 'text') {
    case 'select':
      return (
        <>
          <FilterSelect
            controlId={idChamp}
            labelKey={champ.labelKey}
            placeholderKey={invite()}
            options={champ.options ?? []}
            value={valeur}
            onSelect={poser}
            invalid={invalide}
            describedBy={decrit}
          />
          <input type="hidden" {...liaison} />
        </>
      );
    case 'date':
      return (
        <>
          <FilterDateField
            controlId={idChamp}
            labelKey={champ.labelKey}
            placeholderKey={invite()}
            value={valeur}
            onSelect={poser}
            invalid={invalide}
            describedBy={decrit}
          />
          <input type="hidden" {...liaison} />
        </>
      );
    default:
      if (enPilule) {
        return (
          <FilterTextField
            controlId={idChamp}
            labelKey={champ.labelKey}
            invalid={invalide}
            describedBy={decrit}
            registration={liaison}
          />
        );
      }
      return (
        <>
          <label className="form__label" htmlFor={idChamp}>
            {libelle}
          </label>
          <input
            className="form__input"
            id={idChamp}
            type="text"
            aria-invalid={invalide ? true : undefined}
            aria-describedby={decrit}
            {...liaison}
          />
        </>
      );
  }
}
